import { useEffect, useState, type RefObject } from 'react';

/**
 * Returns true when the referenced element's width drops below `threshold` px.
 * Uses ResizeObserver so it reacts to the container size, not the viewport.
 * Falls back to a one-time getBoundingClientRect read when ResizeObserver is
 * unavailable (e.g. jsdom in Vitest).
 */
export const useContainerNarrow = (ref: RefObject<Element | null>, threshold: number): boolean => {
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Guard: jsdom (Vitest) and some older environments don't implement ResizeObserver.
    // Fall back to a single synchronous measurement — no live updates, but safe.
    if (typeof ResizeObserver === 'undefined') {
      setIsNarrow(el.getBoundingClientRect().width < threshold);
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      setIsNarrow(entry.contentRect.width < threshold);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, threshold]);

  return isNarrow;
};
